import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  CreditCard,
  Gift,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import {
  useProjectWithBilling,
  useUpdateBillingStatus,
  useRestoreProject,
  useProjectBillingHistory,
  useAnalysisRunProgress,
  useForceRerunAnalysis,
  useProjectFilters,
  isAnalysisRunning,
} from '@/hooks/api/useProjects';
import { useHubspotStatus } from '@/hooks/api/useBilling';
import { useToast } from '@/components/ui/use-toast';
import { Progress } from '@/components/ui/progress';
import type { AnalysisRunPhase, UpdateBillingStatusPayload } from '@/api/projects';
import { BillingStatus, projectsApi } from '@/api/projects';
import { CopyableId } from '@/components/CopyableId';
import { HubspotEntitySearchInput } from '@/components/HubspotEntitySearchInput';
import Routes from '@/router/routes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { BadgeProps } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { TranslationsCard } from './TranslationsCard';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { formatDate, formatDateTime } from '@/lib/date';
import { getBillingBadgeVariant, getBillingStatusLabel } from '@/utils/billingDisplay';

// 'NEW' is a form-only sentinel for the "New" status (= billingStatus null).
// Radix Select can't hold a real null/empty value, and '' is reserved here
// for "nothing chosen yet" so the Save button stays disabled until the admin
// picks something.
type BillingStatusFormValue = BillingStatus | 'NEW' | '';

interface UpdateBillingFormState {
  billingStatus: BillingStatusFormValue;
  notes: string;
  billingAmount: string;
  billingCurrency: string;
  billingReference: string;
  hubspotDealId: string;
}

const ANALYSIS_PHASE_LABELS: Record<AnalysisRunPhase, string> = {
  idle: 'Idle',
  per_question_pages: 'Generating per-question pages',
  meta_synthesis: 'Synthesizing cross-question insights',
  overall: 'Generating overall analysis',
  complete: 'Complete',
};

// Scope-picker sentinel for the default main/unfiltered re-run (TELV1-500).
// Never a real filter `_id`, so it can't collide with one.
const MAIN_ANALYSIS_SCOPE = 'main';

function formatElapsed(elapsedMs?: number): string | null {
  if (elapsedMs === undefined || elapsedMs === null) return null;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function getAnalysisBadge(running: boolean, phase?: AnalysisRunPhase): JSX.Element {
  if (running) return <Badge variant="info">Running</Badge>;
  if (phase === 'complete') return <Badge variant="success">Completed</Badge>;
  return <Badge variant="default">Idle</Badge>;
}

export default function SuperAdminSingleProject() {
  const { project_id } = useParams<{ project_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [updateBillingDialogOpen, setUpdateBillingDialogOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [billingForm, setBillingForm] = useState<UpdateBillingFormState>({
    billingStatus: '',
    notes: '',
    billingAmount: '',
    billingCurrency: 'EUR',
    billingReference: '',
    hubspotDealId: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
    confirmLabel?: string;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });
  // Single-filter re-run scope picker (TELV1-500). Defaults to the main/
  // unfiltered analysis, so an admin who ignores the picker gets exactly
  // today's behavior.
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  const [rerunScope, setRerunScope] = useState<string>(MAIN_ANALYSIS_SCOPE);

  // Stable filter reference — inline objects cause infinite re-renders
  // because React Query treats each new object reference as a key change.
  // Memoized on historyPage so pagination still triggers a refetch when
  // the user clicks through billing history. Matches the pattern applied
  // in SuperAdminSingleWorkspace by #1423.
  const billingHistoryFilters = useMemo(() => ({ page: historyPage, limit: 10 }), [historyPage]);

  const { data: project, isLoading, isError, refetch } = useProjectWithBilling(project_id || '');
  const { data: billingHistory, isLoading: historyLoading } = useProjectBillingHistory(
    project_id || '',
    billingHistoryFilters,
  );
  const updateBillingStatus = useUpdateBillingStatus();
  const restoreProject = useRestoreProject();
  const forceRerunAnalysis = useForceRerunAnalysis();
  const { data: analysisProgress } = useAnalysisRunProgress(project_id || '');
  const { data: projectFilters } = useProjectFilters(project_id || '', rerunDialogOpen);
  const { data: hubspotStatus } = useHubspotStatus();
  const { toast } = useToast();

  const analysisRunning = isAnalysisRunning(analysisProgress);

  // Toast when a run we were watching ends. The progress phase alone can't
  // distinguish an errored run from a completion the poller missed (both
  // eventually read 'idle'), so on the running→terminal transition we ask
  // the state endpoint for the authoritative outcome.
  const prevPhaseRef = useRef<AnalysisRunPhase | undefined>(undefined);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    const current = analysisProgress?.phase;
    prevPhaseRef.current = current;
    if (!prev || prev === current || !project_id) return undefined;
    const prevWasRunning = prev !== 'idle' && prev !== 'complete';
    const nowTerminal = current === 'complete' || current === 'idle';
    if (!prevWasRunning || !nowTerminal) return undefined;

    let cancelled = false;
    projectsApi
      .getAnalysisRunState(project_id)
      .then(({ outcome }) => {
        if (cancelled) return;
        if (outcome === 'error') {
          toast({
            title: 'Analysis re-run failed',
            description: 'The pipeline did not complete. Check the analyzer logs.',
            variant: 'destructive',
          });
        } else if (outcome === 'partial_success') {
          toast({
            title: 'Analysis re-run completed with issues',
            description:
              'Some questions could not be analyzed. The rest of the analysis was regenerated.',
          });
        } else {
          toast({
            title: 'Analysis re-run complete',
            description: 'The analysis has been fully regenerated.',
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast({
          title: 'Analysis re-run finished',
          description: 'The run ended, but its outcome could not be determined.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [analysisProgress?.phase, project_id, toast]);

  const handleOpenForceRerun = () => {
    if (!project_id || !project) return;
    setRerunScope(MAIN_ANALYSIS_SCOPE);
    setRerunDialogOpen(true);
  };

  const handleConfirmForceRerun = () => {
    if (!project_id) return;
    const filterId = rerunScope === MAIN_ANALYSIS_SCOPE ? undefined : rerunScope;
    const filterName =
      rerunScope === MAIN_ANALYSIS_SCOPE
        ? undefined
        : projectFilters?.find((f) => f._id === rerunScope)?.name;
    forceRerunAnalysis.mutate(
      { id: project_id, filterId, filterName },
      { onSuccess: () => setRerunDialogOpen(false) },
    );
  };

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_projects.path);
  };

  const handleRestore = () => {
    if (!project_id || !project) return;
    setConfirmDialog({
      open: true,
      title: 'Restore Project',
      description: `Are you sure you want to restore "${project.title}"? The project will be accessible again.`,
      onConfirm: () => {
        restoreProject.mutate(
          {
            id: project_id,
            organizationId: project.organizationId,
            workspaceId: project.workspaceId,
          },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleOpenUpdateBilling = () => {
    setBillingForm({
      // QUOTED is no longer a selectable option — leave the field
      // unselected so a legacy-Quoted project forces an explicit choice
      // rather than silently re-submitting a retired status.
      billingStatus:
        project?.billingStatus === BillingStatus.QUOTED ? '' : project?.billingStatus ?? 'NEW',
      notes: project?.billingNotes || '',
      // Prefill the current values so opening the dialog to change ONE field
      // doesn't blank the rest (the partial-update backend keeps untouched
      // fields, but an empty pre-fill misrepresents the saved state to the
      // admin). Amount is stringified for the text input; currency keeps its
      // EUR default only when unset.
      billingAmount:
        project?.billingAmount !== undefined && project?.billingAmount !== null
          ? String(project.billingAmount)
          : '',
      billingCurrency: project?.billingCurrency || 'EUR',
      billingReference: project?.billingReference || '',
      hubspotDealId: project?.hubspotDealId || '',
    });
    setUpdateBillingDialogOpen(true);
  };

  const handleSubmitBilling = () => {
    if (!project_id || !project || !billingForm.billingStatus) return;
    const payload: UpdateBillingStatusPayload = {
      billingStatus: billingForm.billingStatus === 'NEW' ? null : billingForm.billingStatus,
    };
    // The dialog prefills every field, so blanking one is a deliberate clear.
    // The partial-update backend treats `undefined` as "leave untouched" and
    // `null` as "clear" — so an emptied field must send explicit null when the
    // project previously had a value, otherwise the clear silently no-ops and
    // the stale value persists (PR #1695 review). Truly-untouched empties stay
    // absent to avoid needless writes.
    const notes = billingForm.notes.trim();
    if (notes) payload.notes = notes;
    else if (project.billingNotes) payload.notes = null;

    const amount = billingForm.billingAmount.trim();
    if (amount) {
      const parsed = parseFloat(amount);
      if (!Number.isNaN(parsed)) payload.billingAmount = parsed;
    } else if (project.billingAmount !== undefined && project.billingAmount !== null) {
      payload.billingAmount = null;
    }

    const currency = billingForm.billingCurrency.trim();
    if (currency) payload.billingCurrency = currency;
    else if (project.billingCurrency) payload.billingCurrency = null;

    const reference = billingForm.billingReference.trim();
    if (reference) payload.billingReference = reference;
    else if (project.billingReference) payload.billingReference = null;

    if (billingForm.hubspotDealId.trim()) {
      payload.hubspotDealId = billingForm.hubspotDealId.trim();
    } else if (project.hubspotDealId) {
      payload.hubspotDealId = null; // field was cleared — explicit unlink
    }

    updateBillingStatus.mutate(
      { id: project_id, payload },
      {
        onSuccess: () => {
          setUpdateBillingDialogOpen(false);
        },
      },
    );
  };

  const getStatusBadgeVariant = (status?: string): BadgeProps['variant'] => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col gap-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-red-600">Project not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'Projects', path: Routes.super_admin_dashboard_projects.path },
          { label: project.title },
        ]}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{project.title}</h1>
              <Badge variant={getStatusBadgeVariant(project.status)}>{project.status}</Badge>
              {project.deletedAt && <Badge variant="deleted">Deleted</Badge>}
            </div>
            <p className="text-muted-foreground mt-1">
              <Link
                to={`${Routes.super_admin_dashboard_organizations.path}/${project.organizationId}`}
                className="hover:underline text-blue-600"
              >
                {project.organizationName}
              </Link>
              {' / '}
              <Link
                to={`${Routes.super_admin_dashboard_workspaces.path}/${project.workspaceId}`}
                className="hover:underline text-blue-600"
              >
                {project.workspaceName}
              </Link>
            </p>
          </div>
        </div>
        {project.deletedAt && (
          <Button onClick={handleRestore} disabled={restoreProject.isPending}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Restore Project
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Project Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={getStatusBadgeVariant(project.status)}>{project.status}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Respondents</span>
                  <span className="font-semibold">
                    {project.respondentsCount || 0} / {project.respondentsMax || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Conversations</span>
                  <span className="font-semibold">
                    {project.conversationsCompleted || 0} / {project.conversationsTotal || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Project Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Created</span>
                  <p className="font-medium">{formatDateTime(project.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Organization</span>
                  <p className="font-medium">
                    <Link
                      to={`${Routes.super_admin_dashboard_organizations.path}/${project.organizationId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {project.organizationName}
                    </Link>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Workspace</span>
                  <p className="font-medium">
                    <Link
                      to={`${Routes.super_admin_dashboard_workspaces.path}/${project.workspaceId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {project.workspaceName}
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">IDs</h3>
              <div className="space-y-3">
                <CopyableId value={project._id} label="Project ID" />
                <CopyableId value={project.workspaceId} label="Workspace ID" />
                <CopyableId value={project.organizationId} label="Organization ID" />
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Analysis</h3>
                {getAnalysisBadge(analysisRunning, analysisProgress?.phase)}
              </div>

              {analysisRunning ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="font-medium">
                      {ANALYSIS_PHASE_LABELS[analysisProgress!.phase]}
                    </span>
                  </div>
                  {!!analysisProgress?.total && (
                    <div className="space-y-1">
                      <Progress
                        value={((analysisProgress.current || 0) / analysisProgress.total) * 100}
                      />
                      <p className="text-xs text-muted-foreground text-right">
                        {analysisProgress.current || 0} / {analysisProgress.total}
                      </p>
                    </div>
                  )}
                  {formatElapsed(analysisProgress?.elapsed_ms) && (
                    <p className="text-sm text-muted-foreground">
                      Elapsed: {formatElapsed(analysisProgress?.elapsed_ms)}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Regenerate the full analysis from existing conversations — for example after the
                    analysis language changed on a completed project. The dashboard only offers a
                    re-run when there are new respondents; this button always re-runs.
                  </p>
                  {analysisProgress?.completed_at && (
                    <p className="text-sm text-muted-foreground">
                      Last run completed {formatDateTime(analysisProgress.completed_at)}
                    </p>
                  )}
                  <Button
                    onClick={handleOpenForceRerun}
                    disabled={forceRerunAnalysis.isPending}
                    className="w-full"
                    variant="outline"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Force Re-run Analysis
                  </Button>
                </div>
              )}
            </div>

            {/* TELV1-503 — translation coverage + forced retry. Rendered
                inside the tab content ON PURPOSE: the card owns a summary
                poll, and Radix unmounting inactive tabs is what stops it. */}
            {project_id && <TranslationsCard projectId={project_id} />}
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-6">
          <div className="flex flex-col gap-6">
            {/* Current Billing Status Card */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Current Billing Status</h3>
                <Button onClick={handleOpenUpdateBilling} size="sm">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Update Billing
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <span className="text-muted-foreground text-sm">Status</span>
                  <div className="mt-1">
                    <Badge variant={getBillingBadgeVariant(project.billingStatus)}>
                      {getBillingStatusLabel(project.billingStatus)}
                    </Badge>
                  </div>
                </div>

                {project.billingStatusUpdatedAt && (
                  <div>
                    <span className="text-muted-foreground text-sm">Last Updated</span>
                    <p className="font-medium mt-1">
                      {formatDateTime(project.billingStatusUpdatedAt)}
                    </p>
                  </div>
                )}

                {project.billingStatusUpdatedBy?.email && (
                  <div>
                    <span className="text-muted-foreground text-sm">Updated By</span>
                    <p className="font-medium mt-1">
                      {project.billingStatusUpdatedBy.name || project.billingStatusUpdatedBy.email}
                    </p>
                  </div>
                )}

                {project.billingAmount !== undefined && project.billingAmount !== null && (
                  <div>
                    <span className="text-muted-foreground text-sm">Amount</span>
                    <p className="font-medium mt-1">
                      {formatCurrency(project.billingAmount, project.billingCurrency)}
                    </p>
                  </div>
                )}

                {project.billingReference && (
                  <div>
                    <span className="text-muted-foreground text-sm">Reference</span>
                    <p className="font-medium mt-1 font-mono text-sm">{project.billingReference}</p>
                  </div>
                )}

                {project.hubspotDealId && (
                  <div>
                    <span className="text-muted-foreground text-sm">HubSpot Deal</span>
                    <p className="font-medium mt-1 text-sm">
                      {hubspotStatus?.portalId ? (
                        <a
                          href={`https://app.hubspot.com/contacts/${hubspotStatus.portalId}/deal/${project.hubspotDealId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1 font-mono"
                        >
                          {project.hubspotDealId}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span className="font-mono">{project.hubspotDealId}</span>
                      )}
                    </p>
                  </div>
                )}

                {project.billingInvoicedSnapshot && (
                  <div>
                    <span className="text-muted-foreground text-sm">Invoiced Snapshot</span>
                    <p className="font-medium mt-1 text-sm">
                      {project.billingInvoicedSnapshot.completedCount} completed on{' '}
                      {formatDate(project.billingInvoicedSnapshot.at)}
                    </p>
                  </div>
                )}

                {project.billingNotes && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <span className="text-muted-foreground text-sm">Notes</span>
                    <p className="font-medium mt-1 text-sm">{project.billingNotes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Billing History Timeline */}
            <div className="bg-card rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold">Billing History</h3>
              </div>

              {historyLoading && (
                <div className="p-4 space-y-4">
                  {['top', 'middle', 'bottom'].map((slot) => (
                    <Skeleton key={slot} className="h-16 w-full" />
                  ))}
                </div>
              )}
              {!historyLoading && (!billingHistory || billingHistory.data.length === 0) && (
                <div className="p-8 text-center">
                  <p className="text-muted-foreground">No billing history yet.</p>
                </div>
              )}
              {!historyLoading && !!billingHistory && billingHistory.data.length > 0 && (
                <div className="p-6">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-border" />

                    <div className="space-y-6">
                      {billingHistory.data.map((entry) => (
                        <div key={entry._id} className="relative pl-12">
                          {/* Timeline dot */}
                          <div className="absolute left-2.5 top-1.5 h-3 w-3 rounded-full border-2 border-card bg-muted-foreground ring-2 ring-border" />

                          <div className="bg-muted rounded-lg p-4 border border-border">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <Badge variant={getBillingBadgeVariant(entry.billingStatus)}>
                                {getBillingStatusLabel(entry.billingStatus)}
                              </Badge>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(entry.updatedAt)}
                              </span>
                            </div>

                            <div className="space-y-1">
                              {entry.updatedBy && (
                                <p className="text-sm text-muted-foreground">
                                  By {entry.updatedBy.name || entry.updatedBy.email}
                                </p>
                              )}
                              {entry.billingAmount !== undefined &&
                                entry.billingAmount !== null && (
                                  <p className="text-sm text-muted-foreground">
                                    Amount:{' '}
                                    {formatCurrency(entry.billingAmount, entry.billingCurrency)}
                                  </p>
                                )}
                              {entry.billingReference && (
                                <p className="text-sm text-muted-foreground font-mono">
                                  Ref: {entry.billingReference}
                                </p>
                              )}
                              {entry.notes && (
                                <p className="text-sm text-foreground mt-2 italic">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {billingHistory.totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-border mt-6 pt-4">
                      <div className="text-sm text-muted-foreground">
                        Page {billingHistory.page} of {billingHistory.totalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage((p) => p - 1)}
                          disabled={billingHistory.page === 1}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage((p) => p + 1)}
                          disabled={billingHistory.page === billingHistory.totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Update Billing Dialog */}
      <Dialog open={updateBillingDialogOpen} onOpenChange={setUpdateBillingDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Update Billing</DialogTitle>
            <DialogDescription>
              Update the billing status and details for this project.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="billing-status">Billing Status</Label>
              <Select
                value={billingForm.billingStatus}
                onValueChange={(value) =>
                  setBillingForm((f) => ({ ...f, billingStatus: value as BillingStatusFormValue }))
                }
              >
                <SelectTrigger id="billing-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">New</SelectItem>
                  <SelectItem value={BillingStatus.INVOICED}>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Invoiced
                    </div>
                  </SelectItem>
                  <SelectItem value={BillingStatus.PAID}>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Paid
                    </div>
                  </SelectItem>
                  <SelectItem value={BillingStatus.FREE}>
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4" />
                      Free
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="billing-amount">Amount (optional)</Label>
                <Input
                  id="billing-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={billingForm.billingAmount}
                  onChange={(e) => setBillingForm((f) => ({ ...f, billingAmount: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-currency">Currency</Label>
                <Select
                  value={billingForm.billingCurrency}
                  onValueChange={(value) =>
                    setBillingForm((f) => ({ ...f, billingCurrency: value }))
                  }
                >
                  <SelectTrigger id="billing-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing-reference">Invoice Reference (optional)</Label>
              <Input
                id="billing-reference"
                placeholder="INV-2024-001"
                value={billingForm.billingReference}
                onChange={(e) =>
                  setBillingForm((f) => ({ ...f, billingReference: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hubspot-deal-id">HubSpot Deal (optional)</Label>
              {hubspotStatus?.configured && (
                <HubspotEntitySearchInput
                  kind="deal"
                  onSelect={(id) => setBillingForm((f) => ({ ...f, hubspotDealId: id }))}
                  disabled={updateBillingStatus.isPending}
                />
              )}
              <Input
                id="hubspot-deal-id"
                placeholder="e.g. 987654321"
                value={billingForm.hubspotDealId}
                onChange={(e) => setBillingForm((f) => ({ ...f, hubspotDealId: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                {hubspotStatus?.configured
                  ? 'Selected from search above, or paste a deal id manually. Clear to unlink.'
                  : 'HubSpot lookups are not configured — paste the deal id manually.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing-notes">Notes (optional)</Label>
              <Textarea
                id="billing-notes"
                placeholder="Add any billing notes..."
                value={billingForm.notes}
                onChange={(e) => setBillingForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateBillingDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBilling}
              disabled={!billingForm.billingStatus || updateBillingStatus.isPending}
            >
              {updateBillingStatus.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={restoreProject.isPending}
      />

      {/* Force Re-run Analysis Dialog — single-filter scope picker (TELV1-500) */}
      <Dialog
        open={rerunDialogOpen}
        onOpenChange={(open) => {
          if (forceRerunAnalysis.isPending && !open) return;
          setRerunDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Force Re-run Analysis</DialogTitle>
            <DialogDescription>
              {`This regenerates the entire analysis for "${project.title}" from its existing conversations — no new respondents are needed. ` +
                "The current analysis pages are overwritten (in the project's current analysis language), saved comparisons are invalidated, " +
                'and AI quota is consumed. This takes a few minutes. Respondent summaries and transcript translations are not regenerated.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-2">
            <Label htmlFor="rerun-scope">Analysis to re-run</Label>
            <Select value={rerunScope} onValueChange={setRerunScope}>
              <SelectTrigger id="rerun-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MAIN_ANALYSIS_SCOPE}>Main analysis (unfiltered)</SelectItem>
                {(projectFilters ?? []).map((filter) => (
                  <SelectItem key={filter._id} value={filter._id}>
                    {filter.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRerunDialogOpen(false)}
              disabled={forceRerunAnalysis.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmForceRerun} disabled={forceRerunAnalysis.isPending}>
              {forceRerunAnalysis.isPending ? 'Processing...' : 'Re-run Analysis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
